import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getReleases } from '../services/releaseService';
import './Footer.css';

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const Footer = ({ currentRelease, featureGroupId }) => {
  const [showReleasePopup, setShowReleasePopup] = useState(false);
  const [futureReleases, setFutureReleases] = useState([]);
  const [showFutureReleases, setShowFutureReleases] = useState(false);
  const [loadingFutureReleases, setLoadingFutureReleases] = useState(false);

  const handleSubversionClick = (e) => {
    e.preventDefault();
    setShowReleasePopup(true);
  };

  const closePopup = () => {
    setShowReleasePopup(false);
    setShowFutureReleases(false);
  };

  const loadFutureReleases = useCallback(async () => {
    if (!featureGroupId) return;
    
    try {
      setLoadingFutureReleases(true);
      const allReleases = await getReleases(featureGroupId);
      
      // Filter: not Draft or Live, and not the current release, sorted by End Date ascending
      const future = allReleases
        .filter(release => {
          // Exclude current release
          if (currentRelease && release.id === currentRelease.id) {
            return false;
          }
          // Exclude Draft and Live statuses
          const status = release.status?.toLowerCase();
          return status !== 'draft' && status !== 'live';
        })
        .sort((a, b) => {
          // Sort by end_date ascending, nulls last
          if (!a.end_date && !b.end_date) return 0;
          if (!a.end_date) return 1;
          if (!b.end_date) return -1;
          return new Date(a.end_date) - new Date(b.end_date);
        });
      
      setFutureReleases(future);
    } catch (err) {
      console.error('Error loading future releases:', err);
      setFutureReleases([]);
    } finally {
      setLoadingFutureReleases(false);
    }
  }, [featureGroupId, currentRelease]);

  // Load future releases when popup opens
  useEffect(() => {
    if (showReleasePopup && featureGroupId) {
      loadFutureReleases();
    }
  }, [showReleasePopup, featureGroupId, loadFutureReleases]);

  // Get display version - prefer current_subversion from release, fallback to hardcoded
  const displayVersion = currentRelease?.current_subversion || '1.1.2';
  const hasCurrentRelease = !!currentRelease;
  const hasVersion = !!currentRelease?.current_subversion;
  const hasReleaseInfo = currentRelease && (currentRelease.comments || currentRelease.name);

  return (
    <footer className="app-footer">
      <div className="footer-content">
        <div className="footer-links">
          <Link to="/about">About Us</Link>
          <span className="footer-separator">|</span>
          <Link to="/privacy">Privacy</Link>
          <span className="footer-separator">|</span>
          <Link to="/terms">Terms of Use</Link>
        </div>
        <div className="footer-copyright">
          OpenWonder LLC - Copyright (c) 2025
          {hasCurrentRelease && hasVersion ? (
            <>
              {' - '}
              <button 
                className="version-link"
                onClick={handleSubversionClick}
                title={hasReleaseInfo ? "View release notes" : "View release info"}
              >
                v{displayVersion}
              </button>
            </>
          ) : hasCurrentRelease && !hasVersion ? (
            <>
              {' - '}
              <button 
                className="version-link"
                onClick={handleSubversionClick}
                title="View release info (no version set)"
              >
                {currentRelease.name}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {showReleasePopup && currentRelease && (
        <div className="release-popup-overlay" onClick={closePopup}>
          <div className="release-popup" onClick={(e) => e.stopPropagation()}>
            <div className="release-popup-header">
              <h3>Release Information</h3>
              <button className="popup-close-button" onClick={closePopup}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="release-popup-content">
              <div className="release-popup-section">
                <h1 className="release-popup-h1">Current Major Release: {currentRelease.name}</h1>
                {currentRelease.public_description && currentRelease.public_description.trim() ? (
                  <p>{currentRelease.public_description}</p>
                ) : (
                  <p className="no-content-message">No description available.</p>
                )}
              </div>
              {currentRelease.current_subversion && currentRelease.current_subversion.trim() && (
                <div className="release-popup-section">
                  <h3 className="release-popup-h3">Minor release: {currentRelease.current_subversion}</h3>
                  {currentRelease.comments && currentRelease.comments.trim() ? (
                    <pre className="release-popup-comments">{currentRelease.comments}</pre>
                  ) : (
                    <p className="no-content-message">No release comments available.</p>
                  )}
                  {currentRelease.updated_at && (
                    <p className="release-last-updated">
                      <span className="last-updated-label">Last updated:</span> {formatDateTime(currentRelease.updated_at)}
                    </p>
                  )}
                </div>
              )}
              
              {/* Future Releases Section */}
              {futureReleases.length > 0 && (
                <div className="release-popup-section future-releases-section">
                  <button
                    className="future-releases-toggle"
                    onClick={() => setShowFutureReleases(!showFutureReleases)}
                  >
                    <span>Future Releases</span>
                    <i className={`fas fa-chevron-${showFutureReleases ? 'up' : 'down'}`}></i>
                  </button>
                  {showFutureReleases && (
                    <div className="future-releases-list">
                      {loadingFutureReleases ? (
                        <p className="loading-message">Loading future releases...</p>
                      ) : (
                        futureReleases.map(release => (
                          <div key={release.id} className="future-release-item">
                            <h4 className="future-release-name">{release.name}</h4>
                            {release.public_description && release.public_description.trim() ? (
                              <p className="future-release-description">{release.public_description}</p>
                            ) : (
                              <p className="no-content-message">No description available.</p>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
